import { displayCurrency, displayDistributions } from 'app/scripts/PricingHelper';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import ObjectInfo from 'assets/templates/objectInfo/ObjectInfo';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { isEmpty, sumBy, values } from 'lodash';
import Subfee from 'models/productManagement/subfee/Model.Subfee';

type Props = {
  contract: Model.IContract;
};

export default function ClaimJobPaymentItemInfoContractDetails({ contract }: Props) {
  const { lang } = getLocales();

  if (!contract) {
    return null;
  }

  const { payload } = useApi({
    action: Subfee.list,
    body: {
      id: contract?.subfees?.map((it) => Number(it.subfeeId)),
    },
  });
  const subfees = payload?.data;

  return (
    <>
      <h3>{lang.contractInfo}</h3>
      <div>
        {lang.appNumber}: <b>{contract.appNumber ?? 'N/A'}</b>
      </div>
      <div>
        {lang.status}: <b>{contract.statusCode ?? 'N/A'}</b>
      </div>
      <div>
        {lang.effectiveDate}:{' '}
        <b>{contract.effectiveDate ? localizeDate(contract.effectiveDate, defaultDateTimeFormat) : 'N/A'}</b>
      </div>
      <div>
        {lang.expiryDate}:{' '}
        <b>{contract.expiryDate ? localizeDate(contract.expiryDate, defaultDateTimeFormat) : 'N/A'}</b>
      </div>
      <div>
        {lang.dealership}: <b>{contract.dealer?.name ?? 'N/A'}</b>
      </div>
      <div>
        {lang.distributor}: <b>{contract.dealer?.representative?.name ?? 'N/A'}</b>
      </div>
      <div>
        {lang.underwriter}: <b>{contract.product?.provider?.name ?? 'N/A'}</b>
      </div>

      {contract.subfees && (
        <>
          <h3>{lang.surcharges}</h3>
          <DataMap
            data={contract.subfees}
            render={({ data: subfee }) => (
              <div key={subfee?.subfeeId}>
                {subfees?.find((s) => s.id.toString() === subfee?.subfeeId?.toString())?.name || lang.unknown}:{' '}
                <b>
                  {subfee?.quantity} x <FormatValue value={displayCurrency(subfee?.amount, subfee?.currency)} />
                </b>
              </div>
            )}
          />
          <hr />
          <div>
            {lang.total}:{' '}
            <b>
              <FormatValue value={`${(contract.surchargesPriceCAD || 0).toFixed(2)}$`} />
            </b>
          </div>
        </>
      )}

      {contract.contractEnhancements && !isEmpty(contract.contractEnhancements) && (
        <>
          <h1>{lang.enhancements}</h1>
          <DataMap
            data={contract.contractEnhancements}
            render={({ data: contractEnhancement }) => (
              <div key={contractEnhancement?.productEnhancementId}>
                <h2>{contractEnhancement?.productEnhancement?.name || lang.unknown}: </h2>
                <div>
                  {lang.price}:{' '}
                  <b>
                    <FormatValue value={`${(contractEnhancement.priceCAD || 0).toFixed(2)}$`} />
                  </b>
                </div>
                {contractEnhancement.originalPriceCAD !== contractEnhancement.priceCAD && (
                  <div>
                    {lang.basePrice}:{' '}
                    <b>
                      <FormatValue value={`${(contractEnhancement.originalPriceCAD || 0).toFixed(2)}$`} />
                    </b>
                  </div>
                )}
                {!!contractEnhancement.discountPriceCAD && (
                  <div>
                    {lang.adjustedPrice}:{' '}
                    <b>
                      <FormatValue value={`${(contractEnhancement.discountPriceCAD || 0).toFixed(2)}$`} />
                    </b>
                  </div>
                )}
                {!!contractEnhancement.distributorPriceCAD && (
                  <div>
                    {lang.distributorPrice}:{' '}
                    <b>
                      <FormatValue value={`${(contractEnhancement.distributorPriceCAD || 0).toFixed(2)}$`} />
                    </b>
                  </div>
                )}
                {!!values(contractEnhancement.pricingDistributions).length && (
                  <ObjectInfo
                    object={displayDistributions(contractEnhancement.pricingDistributions)}
                    objectName={lang.pricingDistributions}
                    disableTranslation
                  />
                )}
                {!!values(contractEnhancement.distributorDistributions).length && (
                  <ObjectInfo
                    object={displayDistributions(contractEnhancement.distributorDistributions)}
                    objectName={lang.distributorDistributions}
                    disableTranslation
                  />
                )}
              </div>
            )}
          />
          <hr />
          <div>
            {lang.total}:{' '}
            <b>
              <FormatValue value={`${sumBy(contract.contractEnhancements, (it) => it.priceCAD).toFixed(2)}$`} />
            </b>
          </div>
        </>
      )}

      <h3>{lang.vehicle}</h3>
      <div>
        {lang.vin}: <b>{contract.vehicle?.vin ?? 'N/A'}</b>
      </div>
      <div>
        {lang.purchaseState}: <b>{contract.vehicle?.purchaseState ?? 'N/A'}</b>
      </div>
      <div>
        {lang.inServiceDate}:{' '}
        <b>
          {contract.vehicle?.inServiceDate
            ? localizeDate(contract.vehicle?.inServiceDate, defaultDateTimeFormat)
            : 'N/A'}
        </b>
      </div>
    </>
  );
}
